import { Context, createContext, Provider, useContext } from 'react'
import type { IAnyModelType } from 'mobx-state-tree/dist/types/complex-types/model'
import type { createUseQueryHook } from 'mst-gql'
import type { QueryLike, UseQueryHookOptions } from 'mst-gql/dist/react'
import type { MSTGQLStore } from 'mst-gql/dist/MSTGQLStore'

export type AppStoreConfig = {
  store: IAnyModelType
  useQuery: ReturnType<typeof createUseQueryHook>
}

const HamsterContext: Context<AppStoreConfig> = createContext({} as AppStoreConfig)
const HamsterProvider: Provider<AppStoreConfig> = HamsterContext.Provider

// Hook to access the appStore anywhere
function useAppStore() {
  if (!HamsterContext) {
    throw new Error('AppStoreContext is not initialized. Call init() before invoking useAppStore() ')
  }
  const storeConfig = useContext(HamsterContext)
  if (!storeConfig) {
    throw new Error('useAppStore() can only be used inside the AppStoreContext provider')
  }
  return storeConfig.store as any
}

function useAppStoreConfig() {
  if (!HamsterContext) {
    throw new Error('AppStoreContext is not initialized. Call init() before invoking useAppStore() ')
  }
  const storeConfig = useContext(HamsterContext)
  if (!storeConfig) {
    throw new Error('useAppStore() can only be used inside the AppStoreContext provider')
  }
  return storeConfig
}

/**
 * Expose the useQuery of AppStoreConfig so that it can be used just like the original one.
 * @param query
 * @param options
 */
function useQuery<STORE extends typeof MSTGQLStore.Type, DATA>(
  query?: QueryLike<STORE, DATA>,
  options?: UseQueryHookOptions<STORE>
) {
  if (!HamsterContext) {
    throw new Error('AppStoreContext is not initialized. Call init() before invoking useAppStore() ')
  }
  const storeConfig = useContext(HamsterContext)
  if (!storeConfig) {
    throw new Error('useAppStore() can only be used inside the AppStoreContext provider')
  }
  // TODO: figure out proper typing. as QueryLike<any, DATA> shoudl not be required here
  return storeConfig.useQuery(query as QueryLike<any, DATA>, options)
}

export { useAppStore, useQuery, useAppStoreConfig, HamsterContext, HamsterProvider }
